import React from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { PROFILE_TAB_REVIEWS_FROM_CUSTOMERS } from '../../util/profilePageTabs';
import { ensureUser } from '../../util/data';

import { Heading, H2, NamedLink, Reviews } from '../../components';

import css from './ListingPage.module.css';

/**
 * Seller review preview (max 2 ofProvider reviews from duck).
 * Empty sellers get contextual copy; hide when we cannot resolve a published listing count
 * for the no-sales case.
 *
 * @component
 */
const SectionReviews = props => {
  const {
    reviews = [],
    fetchReviewsError,
    author,
    reviewCount,
    authorPublishedListingCount = null,
  } = props;
  const intl = useIntl();
  const user = ensureUser(author);
  const authorId = user?.id?.uuid;
  const displayName = user?.attributes?.profile?.displayName || '';
  const metadata = user?.attributes?.profile?.metadata || {};
  const metadataCount = Number(metadata.reviewCount);
  // Align with SectionSellerLine: missing sales metadata is unknown, not "zero sales".
  const itemsSoldCount =
    typeof metadata.itemsSoldCount === 'number' ? metadata.itemsSoldCount : null;
  // Prefer a positive metadata aggregate (full seller total). If metadata is 0/missing
  // but we still have preview rows (stale aggregate), fall back to the loaded count.
  const headerCount =
    typeof reviewCount === 'number'
      ? reviewCount
      : Number.isFinite(metadataCount) && metadataCount > 0
      ? metadataCount
      : reviews.length;

  const reviewAverageRaw = metadata.reviewAverage;
  const showAverage = Number.isFinite(reviewAverageRaw) && headerCount > 0;
  const averageFormatted = showAverage
    ? intl.formatNumber(reviewAverageRaw, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    : null;

  const hasReviews = reviews.length > 0;
  const hasSales = itemsSoldCount != null && itemsSoldCount > 0;
  const publishedCount =
    typeof authorPublishedListingCount === 'number' ? authorPublishedListingCount : null;
  // Never pair empty-state claims with a reviews fetch error.
  const showNoSalesEmpty =
    !fetchReviewsError &&
    !hasReviews &&
    itemsSoldCount === 0 &&
    publishedCount != null &&
    publishedCount >= 1;
  const showSalesNoReviewsEmpty = !fetchReviewsError && !hasReviews && hasSales;

  if (!fetchReviewsError && !hasReviews && !showNoSalesEmpty && !showSalesNoReviewsEmpty) {
    return null;
  }

  return (
    <section className={css.sectionReviews}>
      <div className={css.reviewsHeader}>
        <div className={css.sectionHeadingRow}>
          <svg
            className={css.sectionHeaderIcon}
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path
              d="m12 3.2 2.2 5.2 5.5.7-4.1 3.8 1.1 5.4L12 15.7 7.3 18.3l1.1-5.4-4.1-3.8 5.5-.7Z"
              fill="none"
            />
          </svg>
          <Heading as="h2" rootClassName={css.sectionHeading}>
            <FormattedMessage id="ListingPage.reviewsTitle" values={{ count: headerCount }} />
          </Heading>
        </div>
        {showAverage ? (
          <span className={css.reviewsAggregate} aria-hidden="true">
            {averageFormatted}
          </span>
        ) : null}
      </div>
      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}
      {hasReviews ? <Reviews reviews={reviews} /> : null}
      {showSalesNoReviewsEmpty ? (
        <p className={css.reviewsEmptyState}>
          <FormattedMessage
            id="ListingPage.reviewsEmptySalesNoReviews"
            values={{ name: displayName, count: itemsSoldCount }}
          />
        </p>
      ) : null}
      {showNoSalesEmpty ? (
        <p className={css.reviewsEmptyState}>
          <FormattedMessage
            id="ListingPage.reviewsEmptyNoSales"
            values={{ name: displayName, count: publishedCount }}
          />
        </p>
      ) : null}
      {authorId && hasReviews ? (
        <NamedLink
          className={css.seeAllReviews}
          name="ProfilePage"
          params={{ id: authorId }}
          to={{ search: `?tab=${PROFILE_TAB_REVIEWS_FROM_CUSTOMERS}` }}
        >
          <FormattedMessage id="ListingPage.seeAllReviews" />
        </NamedLink>
      ) : null}
    </section>
  );
};

export default SectionReviews;
